import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { toast } from './Toast';
import './CustomerFormModal.css';

function CustomerFormModal({ customer, onClose, onSuccess }) {
  const { isCompanyAdmin } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    phone: '91',
    email: '',
    address: '',
    branchId: '',
    alternatePhone: '91',
    careOf: '',
    secondContact: '',
    secondContactName: '',
    notes: '',
  });
  const [branches, setBranches] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [countryCode, setCountryCode] = useState('91');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [altCountryCode, setAltCountryCode] = useState('91');
  const [altPhoneNumber, setAltPhoneNumber] = useState('');

  useEffect(() => {
    if (isCompanyAdmin()) fetchBranches();
  }, []);

  useEffect(() => {
    if (customer) {
      setFormData({
        name: customer.name || '',
        phone: customer.phone || '91',
        email: customer.email || '',
        address: customer.address || '',
        branchId: customer.branchId || '',
        alternatePhone: customer.alternatePhone || '91',
        careOf: customer.careOf || '',
        secondContact: customer.secondContact || '',
        secondContactName: customer.secondContactName || '',
        notes: customer.notes || '',
      });

      let fullPhone = customer.phone || '';
      let code = '91';
      let num = '';
      if (fullPhone === '91' || !fullPhone) {
        code = '91';
        num = '';
      } else if (fullPhone.startsWith('+')) {
        const match = fullPhone.match(/^\+(\d{1,4})/);
        if (match) {
          code = '+' + match[1];
          num = fullPhone.slice(match[0].length);
        }
      } else if (fullPhone.startsWith('91') && fullPhone.length > 2) {
        code = '91';
        num = fullPhone.slice(2);
      } else {
        code = '91';
        num = fullPhone;
      }
      setCountryCode(code);
      setPhoneNumber(num);

      let fullAlt = customer.alternatePhone || '';
      let altCode = '91';
      let altNum = '';
      if (fullAlt === '91' || !fullAlt) {
        altCode = '91';
        altNum = '';
      } else if (fullAlt.startsWith('+')) {
        const match = fullAlt.match(/^\+(\d{1,4})/);
        if (match) {
          altCode = '+' + match[1];
          altNum = fullAlt.slice(match[0].length);
        }
      } else if (fullAlt.startsWith('91') && fullAlt.length > 2) {
        altCode = '91';
        altNum = fullAlt.slice(2);
      } else {
        altCode = '91';
        altNum = fullAlt;
      }
      setAltCountryCode(altCode);
      setAltPhoneNumber(altNum);
    } else {
      setFormData({
        name: '',
        phone: '91',
        email: '',
        address: '',
        branchId: '',
        alternatePhone: '91',
        careOf: '',
        secondContact: '',
        secondContactName: '',
        notes: '',
      });
      setCountryCode('91');
      setPhoneNumber('');
      setAltCountryCode('91');
      setAltPhoneNumber('');
    }
  }, [customer]);

  const fetchBranches = async () => {
    try {
      const response = await axios.get('/company/branches');
      const data = response.data.data || response.data;
      setBranches(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch branches:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let val = value;
    if (['phone', 'alternatePhone'].includes(name)) {
      val = val.replace(/[a-zA-Z]/g, '');
    }
    setFormData(prev => ({
      ...prev,
      [name]: val
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = (combinedPhone) => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!combinedPhone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^[0-9+\-\s()]{7,20}$/.test(combinedPhone)) {
      newErrors.phone = 'Invalid phone format';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (isCompanyAdmin() && !formData.branchId) {
      newErrors.branchId = 'Branch is required';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      toast.error(Object.values(newErrors)[0]);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const combinedPhone = `${countryCode}${phoneNumber}`;
    const combinedAlternatePhone = altPhoneNumber ? `${altCountryCode}${altPhoneNumber}` : '';

    if (!validateForm(combinedPhone)) {
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...formData,
        phone: combinedPhone,
        alternatePhone: combinedAlternatePhone,
        branchId: formData.branchId ? parseInt(formData.branchId) : null
      };

      if (customer) {
        await axios.put(`/customers/${customer.id}`, payload);
      } else {
        await axios.post('/customers', payload);
      }
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save customer');
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{customer ? 'Edit Customer' : 'Create Customer'}</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          {isCompanyAdmin() && (
            <div className="form-group">
              <label htmlFor="branchId">Branch *</label>
              <select id="branchId" name="branchId" value={formData.branchId}
                onChange={handleChange} className={errors.branchId ? 'error' : ''}
                required>
                <option value="">Select Branch</option>
                {branches.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
              {errors.branchId && <span className="error-message">{errors.branchId}</span>}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={errors.name ? 'error' : ''}
              placeholder="Enter customer name"
              required
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone *</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                id="countryCode"
                name="countryCode"
                placeholder="Code"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value.replace(/[^0-9+]/g, ''))}
                disabled={loading}
                className={errors.phone ? 'error' : ''}
                style={{ width: '80px', flexShrink: 0 }}
                maxLength="5"
                required
              />
              <input
                type="tel"
                id="phone"
                name="phone"
                placeholder="Phone Number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value.replace(/[^0-9]/g, ''))}
                disabled={loading}
                className={errors.phone ? 'error' : ''}
                style={{ flexGrow: 1 }}
                maxLength="15"
                required
              />
            </div>
            {errors.phone && <span className="error-message">{errors.phone}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={errors.email ? 'error' : ''}
              placeholder="Enter email address"
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="address">Address</label>
            <textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              rows="3"
              placeholder="Enter address"
            />
          </div>

          <div className="form-group">
            <label htmlFor="alternatePhone">Alternate Phone</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                id="altCountryCode"
                name="altCountryCode"
                placeholder="Code"
                value={altCountryCode}
                onChange={(e) => setAltCountryCode(e.target.value.replace(/[^0-9+]/g, ''))}
                disabled={loading}
                className={errors.alternatePhone ? 'error' : ''}
                style={{ width: '80px', flexShrink: 0 }}
                maxLength="5"
              />
              <input
                type="tel"
                id="alternatePhone"
                name="alternatePhone"
                placeholder="Alternate Number"
                value={altPhoneNumber}
                onChange={(e) => setAltPhoneNumber(e.target.value.replace(/[^0-9]/g, ''))}
                disabled={loading}
                className={errors.alternatePhone ? 'error' : ''}
                style={{ flexGrow: 1 }}
                maxLength="15"
              />
            </div>
            {errors.alternatePhone && <span className="error-message">{errors.alternatePhone}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="careOf">Care Of (C/O)</label>
            <input type="text" id="careOf" name="careOf"
              value={formData.careOf} onChange={handleChange}
              placeholder="e.g. c/o John Smith" />
          </div>

          <div className="form-group">
            <label htmlFor="secondContact">Second Contact Number</label>
            <input type="tel" id="secondContact" name="secondContact"
              value={formData.secondContact} onChange={handleChange}
              placeholder="Second contact number" />
          </div>

          <div className="form-group">
            <label htmlFor="secondContactName">Second Contact Name</label>
            <input type="text" id="secondContactName" name="secondContactName"
              value={formData.secondContactName} onChange={handleChange}
              placeholder="Second contact person name" />
          </div>

          <div className="form-group">
            <label htmlFor="notes">Notes</label>
            <textarea id="notes" name="notes" value={formData.notes}
              onChange={handleChange} rows="2" placeholder="Any additional notes" />
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : (customer ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CustomerFormModal;
